/**
 * Qué hace falta para dar de alta un cliente, según por dónde le llega el servicio.
 *
 * El formulario tenía todos los campos a la vista al mismo tiempo y obligaba a saberse las
 * reglas de memoria: que la fibra propia va con PPPoE, que el radio va con cola, que a un
 * cliente de proveedor NO se le crea nada en nuestro router. Equivocarse ahí deja a alguien
 * instalado y sin servicio, o cobrándole de más.
 *
 * Aquí están esas reglas escritas una sola vez. El formulario pregunta tres cosas y de
 * ellas sale todo lo demás:
 *
 *   1. ¿Cómo le llega?      radio | fibra
 *   2. ¿De quién es la red?  propia | de un revendedor | de un proveedor
 *      (solo se pregunta para fibra: el radio siempre es red propia)
 *   3. ¿Qué clase de cliente? residencial | empresarial
 *
 * Cerrado con Víctor el 11-ago-2026.
 */

export const ORIGENES = {
  propia:     { icono: '🏗️', texto: 'Mi red propia' },
  revendedor: { icono: '🤝', texto: 'De un revendedor' },
  proveedor:  { icono: '🌐', texto: 'De un proveedor' },
};

/**
 * Todo lo que se deduce de las tres respuestas.
 *
 * @returns {{
 *   tecnologia: 'radio_enlace'|'fibra',
 *   mikrotikTipo: 'pppoe'|'simple_queue'|'ninguno',
 *   enNuestroRouter: boolean,
 *   cobro: 'plan'|'contrato'|'revendedor'|'proveedor',
 *   pideDatosProveedor: boolean,
 *   explicacion: string
 * }}
 */
/** De un revendedor no cuelgan empresas: sus clientes son residenciales. */
export const admiteEmpresarial = (origen) => origen !== 'revendedor';

export const caminoDe = (form) => {
  const origen = form?._origen || 'propia';
  // Aunque quedara marcado «empresarial» de antes, un cliente de revendedor no lo es
  const esEmpresa = form?.tipo_cliente === 'empresarial' && admiteEmpresarial(origen);

  /**
   * En la base convivían «radio» y «radio_enlace» para lo mismo. La migración v52 los
   * unificó, pero se sigue aceptando la vieja: si alguna quedara suelta o entrara por otra
   * vía, el cliente debe verse igual y no aparecer sin tecnología.
   */
  const guardada = form?.tecnologia_instalada === 'radio' ? 'radio_enlace'
                 : (form?.tecnologia_instalada || '');

  // El radio siempre va por nuestra red; proveedor y revendedor siempre son fibra
  const tecnologia = origen === 'propia' ? guardada : 'fibra';

  // A un cliente de proveedor no se le crea nada en nuestro MikroTik: ese servicio no lo
  // prestamos nosotros. Crearle una cuenta aquí sería mentir sobre quién lo atiende.
  if (origen === 'proveedor') {
    return {
      tecnologia: 'fibra',
      mikrotikTipo: 'ninguno',
      enNuestroRouter: false,
      // Ser de proveedor no cambia cómo se le cobra: una empresa lleva su velocidad y su
      // valor acordados, venga el servicio de donde venga. Antes se le ofrecía un plan
      // aunque fuera empresarial.
      cobro: esEmpresa ? 'contrato' : 'proveedor',
      pideDatosProveedor: true,
      explicacion: 'El servicio lo presta el proveedor. No se crea nada en tu MikroTik: '
                 + 'se guardan los datos que él entrega para configurar el equipo del cliente.'
                 + (esEmpresa ? ' Se le cobra la velocidad y el valor acordados, no un plan.' : ''),
    };
  }

  /**
   * En nuestra red, lo que decide PPPoE o cola no es el cliente sino cómo está armado el
   * servicio: la fibra residencial entra con usuario y contraseña; el radio y las empresas
   * van por cola, con su velocidad fija.
   */
  const mikrotikTipo = tecnologia === 'radio_enlace' ? 'simple_queue'
                     : esEmpresa ? 'simple_queue'
                     : tecnologia === 'fibra' ? 'pppoe'
                     : 'ninguno';

  const cobro = esEmpresa ? 'contrato'
              : origen === 'revendedor' ? 'revendedor'
              : 'plan';

  return {
    tecnologia, mikrotikTipo, enNuestroRouter: mikrotikTipo !== 'ninguno',
    cobro, pideDatosProveedor: false,
    explicacion: !tecnologia ? 'Dime primero cómo le llega el servicio.'
      : origen === 'revendedor'
        ? (esEmpresa
            ? 'Va por tu fibra con su cola, y se le cobra lo acordado con el revendedor.'
            : 'Va por tu fibra con su PPPoE, igual que cualquier cliente. Lo único distinto '
            + 'es que el cobro mensual es el acordado con el revendedor.')
      : esEmpresa
        ? (tecnologia === 'radio_enlace'
            ? 'Radio por cola, con la velocidad y el valor que acordaron. No lleva plan.'
            : 'Fibra por cola, con la velocidad y el valor que acordaron. No lleva plan.')
      : tecnologia === 'radio_enlace'
        ? 'Radio por cola, con un plan de radio.'
        : 'Fibra por PPPoE, con un plan de fibra.',
  };
};

/**
 * «radio» y «radio_enlace» son lo mismo escrito de dos formas.
 *
 * Está en los dos lados: en los clientes y en los planes. Comparándolas tal cual, «Radio
 * Rural Básico» —marcado «radio»— desaparecía al elegir un cliente de radio, porque el
 * cliente decía «radio_enlace». Se igualan antes de comparar.
 */
export const mismaTecnologia = (a, b) => {
  const igual = (x) => x === 'radio' ? 'radio_enlace' : x;
  return igual(a) === igual(b);
};

/** Solo los planes que sirven para esta tecnología. Un plan de fibra no va en un radio. */
export const planesDe = (planes, tecnologia) => (planes || [])
  .filter(p => p.activo)
  .filter(p => !tecnologia || !p.tecnologia || p.tecnologia === 'ambas'
            || mismaTecnologia(p.tecnologia, tecnologia));
